export interface LandingFaq {
  question: string;
  answer: string;
}

export interface LandingComparison {
  criterio: string;
  nosotros: string;
  media: string;
}

export interface LocalLandingData {
  slug: string;
  badge: string;
  h1: string;
  h1Highlight: string;
  intro: string;
  investment: string;
  breadcrumbLabel: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  ogDescription: string;
  quickAnswerTitle: string;
  quickAnswer: string;
  serviceName: string;
  serviceDescription: string;
  pillarsTitle: string;
  pillars: { title: string; text: string }[];
  deliverablesTitle: string;
  deliverables: string[];
  comparisonTitle: string;
  comparison: LandingComparison[];
  zonasTitle?: string;
  zonasIntro?: string;
  faqsTitle: string;
  faqs: LandingFaq[];
  relatedTitle: string;
  related: { label: string; path: string; text: string }[];
  ctaTitle: string;
  ctaText: string;
}

/** Distritos y municipios del área metropolitana de Madrid donde operamos. */
export const madridZonas = [
  "Salamanca",
  "Chamberí",
  "Chamartín",
  "Centro",
  "Retiro",
  "Tetuán",
  "Moncloa-Aravaca",
  "Chueca y Justicia",
  "Arganzuela",
  "Hortaleza",
  "Ciudad Lineal",
  "Fuencarral-El Pardo",
  "Pozuelo de Alarcón",
  "Las Rozas",
  "Majadahonda",
  "Alcobendas",
  "San Sebastián de los Reyes",
  "Boadilla del Monte",
  "Getafe",
  "Alcalá de Henares",
];

const marcaFaq: LandingFaq = {
  question: "¿Garantizáis la primera posición en Google en Madrid?",
  answer:
    "No. Ninguna agencia seria puede garantizar posiciones: quien lo hace, miente o usa técnicas de riesgo que acaban en penalización. Lo que sí ejecutamos es el trabajo profesional que las primeras posiciones exigen en un mercado tan competido como Madrid: auditoría técnica completa, arquitectura de contenidos por intención, autoridad ganada con enlaces reales, optimización continua de Core Web Vitals y reporting mensual de facturación.",
};

export const madridLandings: Record<string, LocalLandingData> = {
  "agencia-seo-madrid": {
    slug: "agencia-seo-madrid",
    badge: "Agencia SEO premium en Madrid",
    h1: "Agencia SEO en Madrid.",
    h1Highlight: "Una estrategia propia para tu empresa.",
    intro:
      "Trabajamos con un único cliente por sector en Madrid. Sin promesas de posiciones: ejecución técnica, contenido de autoridad y enlaces reales, con reporting mensual de facturación generada.",
    investment: "Propuesta personalizada tras diagnóstico · Un cliente por sector en Madrid · Plazas limitadas",
    breadcrumbLabel: "Agencia SEO Madrid",
    metaTitle: "Agencia SEO en Madrid | SEOEVOLUTION",
    metaDescription:
      "Agencia SEO premium en Madrid con IA y GEO. Exclusividad por sector, plazas limitadas y propuesta personalizada según mercado y objetivos.",
    metaKeywords:
      "agencia seo madrid, agencia seo en madrid, seo madrid, empresa seo madrid, agencia posicionamiento web madrid",
    ogDescription:
      "Agencia SEO premium en Madrid. Exclusividad por sector, ejecución senior y reporting de facturación. Propuesta según mercado, competencia y objetivos.",
    quickAnswerTitle: "¿Qué es SEOEVOLUTION en Madrid?",
    quickAnswer:
      "SEOEVOLUTION es una agencia SEO premium que trabaja en Madrid con un único cliente por sector. Ejecuta posicionamiento web, SEO local en Google Maps, SEO para ecommerce y GEO (visibilidad en ChatGPT, Perplexity y Gemini). Cada proyecto se diseña según mercado, competencia y objetivos, con compromiso mínimo de 3 meses en SEO local y de 6 meses en GEO, SEO web y ecommerce, atención senior y reporting mensual centrado en facturación, no en métricas de vanidad.",
    serviceName: "Agencia SEO en Madrid",
    serviceDescription:
      "Servicio integral de posicionamiento SEO y GEO para empresas de Madrid: auditoría técnica, contenidos, autoridad y visibilidad en motores de IA.",
    pillarsTitle: "Por qué somos una agencia SEO distinta en Madrid",
    pillars: [
      {
        title: "Exclusividad por sector",
        text: "Si trabajamos tu vertical en Madrid, tu competidor directo no puede contratarnos. Contractual, sin asteriscos.",
      },
      {
        title: "Ejecución senior real",
        text: "Plazas limitadas. Sin juniors rotando ni entregables de plantilla.",
      },
      {
        title: "SEO + GEO en el mismo plan",
        text: "Optimizamos para Google y para los motores generativos que ya responden a tus clientes antes de que hagan clic.",
      },
      {
        title: "Reporting de facturación",
        text: "Medimos euros, leads cualificados y llamadas. Si tu negocio no factura más, hemos fracasado.",
      },
    ],
    deliverablesTitle: "Qué ejecutamos cada mes en un proyecto de Madrid",
    deliverables: [
      "Auditoría técnica continua: indexación, canonicals, Core Web Vitals, schema y canibalizaciones",
      "Mapa de keywords por distrito e intención de búsqueda (informacional, comercial y transaccional)",
      "Arquitectura de contenidos: páginas de servicio, páginas de zona y contenido de autoridad",
      "Optimización de la ficha de Google Business Profile y del pack local en los distritos objetivo",
      "Link building con medios madrileños, sectoriales y nacionales verificables",
      "Optimización GEO: respuestas directas, datos estructurados y entidades para ChatGPT, Perplexity y Gemini",
      "Mejora de velocidad y accesibilidad hasta el objetivo de 0,3 segundos de carga",
      "Reporting mensual con facturación atribuida, leads, llamadas y evolución de visibilidad",
    ],
    comparisonTitle: "SEOEVOLUTION frente a la agencia SEO media de Madrid",
    comparison: [
      { criterio: "Clientes por sector y zona", nosotros: "Uno. Exclusividad contractual", media: "Todos los que entren" },
      { criterio: "Perfil que ejecuta", nosotros: "Senior, siempre el mismo equipo", media: "Junior rotando por cartera" },
      { criterio: "Qué se reporta", nosotros: "Facturación, leads y llamadas", media: "Impresiones y posiciones sueltas" },
      { criterio: "Visibilidad en motores de IA", nosotros: "Incluida en todos los planes", media: "No se trabaja" },
      { criterio: "Promesa comercial", nosotros: "Ejecución profesional documentada", media: "\"Te ponemos el primero\"" },
      { criterio: "Propuesta e inversión", nosotros: "A medida tras diagnóstico", media: "Paquetes cerrados genéricos" },
    ],
    zonasTitle: "Distritos y municipios de Madrid donde operamos",
    zonasIntro:
      "El SEO en Madrid se gana por distrito: la intención de búsqueda en Salamanca no es la de Getafe. Trabajamos el mapa completo del área metropolitana.",
    faqsTitle: "Agencia SEO en Madrid: preguntas frecuentes",
    faqs: [
      {
        question: "¿Cuánto cuesta una agencia SEO en Madrid?",
        answer:
          "Depende del mercado, la competencia del sector, el alcance y los objetivos. Madrid concentra mucha densidad de competidores, así que el trabajo técnico, editorial y de autoridad necesario varía mucho entre sectores y distritos. En SEOEVOLUTION no publicamos tarifas: cada empresa necesita una estrategia propia, y alcance e inversión se concretan tras el diagnóstico.",
      },
      {
        question: "¿Cuánto tarda el SEO en dar resultados en Madrid?",
        answer:
          "Los primeros movimientos de visibilidad aparecen entre el mes 2 y el 3. El impacto real en facturación suele consolidarse entre el mes 6 y el 9, porque Madrid concentra a los competidores más fuertes de España en casi todos los sectores. Estos plazos orientativos no son garantías ni fijan el compromiso contractual: SEO local tiene un mínimo de 3 meses; GEO, SEO web y ecommerce, de 6 meses. La continuidad posterior es opcional y a convenir.",
      },
      {
        question: "¿Trabajáis con negocios físicos de Madrid o solo con empresas online?",
        answer:
          "Con ambos. Para negocios físicos (clínicas, restauración, servicios profesionales, inmobiliarias) trabajamos SEO local y Google Maps con exclusividad por sector y zona. Para empresas de servicios y B2B trabajamos SEO web nacional con base en Madrid. Y para tiendas online, SEO ecommerce.",
      },
      {
        question: "¿Qué es la exclusividad por sector en Madrid?",
        answer:
          "Significa que si una clínica dental de Chamberí nos contrata, no aceptamos otra clínica dental que compita por las mismas búsquedas en esa zona. Es la única forma honesta de trabajar: no se puede posicionar a dos competidores directos a la vez para las mismas keywords.",
      },
      marcaFaq,
    ],
    relatedTitle: "Sigue explorando",
    related: [
      { label: "SEO Local en Madrid", path: "/servicios/seo-local/madrid", text: "Google Maps y pack local por distrito." },
      { label: "Posicionamiento web en Madrid", path: "/posicionamiento-web-madrid", text: "Tráfico cualificado para empresas de servicios y B2B." },
      { label: "Consultor SEO en Madrid", path: "/consultor-seo-madrid", text: "Consultoría senior directa, sin capas intermedias." },
      { label: "Agencia GEO en Madrid", path: "/agencia-geo-madrid", text: "Visibilidad en ChatGPT, Perplexity y Gemini." },
    ],
    ctaTitle: "¿Tu sector sigue libre en Madrid?",
    ctaText:
      "Solo trabajamos con un cliente por sector y zona. Solicita un diagnóstico de tu empresa y te decimos si tu vertical en Madrid sigue disponible y qué inversión necesita.",
  },

  "posicionamiento-web-madrid": {
    slug: "posicionamiento-web-madrid",
    badge: "Posicionamiento web en Madrid",
    h1: "Posicionamiento web en Madrid",
    h1Highlight: "para empresas que quieren facturar más",
    intro:
      "Posicionamiento SEO profesional para empresas de servicios y B2B con base en Madrid. Trabajo técnico, contenido de autoridad y enlaces reales, medido en facturación.",
    investment: "Propuesta personalizada tras diagnóstico · Compromiso mínimo de 6 meses",
    breadcrumbLabel: "Posicionamiento web Madrid",
    metaTitle: "Posicionamiento web en Madrid | SEOEVOLUTION",
    metaDescription:
      "Posicionamiento web y SEO en Madrid para empresas de servicios y B2B. Auditoría técnica, contenidos de autoridad y reporting de facturación. Propuesta a medida.",
    metaKeywords:
      "posicionamiento web madrid, posicionamiento seo madrid, seo madrid, empresa posicionamiento web madrid",
    ogDescription:
      "Posicionamiento web profesional en Madrid. Ejecución técnica senior y reporting de facturación, no de vanity metrics.",
    quickAnswerTitle: "¿Qué incluye el posicionamiento web en Madrid?",
    quickAnswer:
      "El posicionamiento web en Madrid incluye auditoría técnica del dominio, investigación de keywords por intención, optimización on-page de cada página de servicio, creación de contenido de autoridad, construcción de enlaces en medios relevantes y mejora continua de Core Web Vitals. En SEOEVOLUTION añadimos optimización GEO para que la empresa también aparezca en las respuestas de ChatGPT, Perplexity y Gemini. La propuesta se diseña según mercado, competencia y objetivos.",
    serviceName: "Posicionamiento web en Madrid",
    serviceDescription:
      "Servicio de posicionamiento web SEO para empresas de servicios y B2B en Madrid, con foco en tráfico cualificado y facturación.",
    pillarsTitle: "Cómo enfocamos el posicionamiento web en Madrid",
    pillars: [
      { title: "Intención antes que volumen", text: "Priorizamos las búsquedas que traen presupuesto, no las que inflan el informe." },
      { title: "Base técnica impecable", text: "Sin indexación limpia, velocidad y schema correcto, el contenido no rinde." },
      { title: "Contenido con criterio", text: "Páginas escritas por especialistas, no generadas en masa. Google y los LLM distinguen." },
      { title: "Autoridad verificable", text: "Enlaces en medios reales con tráfico, nunca redes privadas ni granjas." },
    ],
    deliverablesTitle: "Entregables del servicio",
    deliverables: [
      "Auditoría técnica inicial de hasta 300 puntos con plan de corrección priorizado",
      "Estudio de keywords y mapa de intención por página",
      "Reescritura y optimización de páginas de servicio existentes",
      "Plan editorial mensual de contenidos de autoridad",
      "Datos estructurados: Organization, Service, FAQPage y BreadcrumbList",
      "Optimización de Core Web Vitals y rendimiento real de carga",
      "Link building sectorial con métricas de autoridad verificables",
      "Informe mensual con leads, formularios, llamadas y facturación atribuida",
    ],
    comparisonTitle: "Qué cambia frente a un proveedor low cost",
    comparison: [
      { criterio: "Auditoría previa", nosotros: "Técnica y manual antes de cotizar", media: "Informe automático de una herramienta" },
      { criterio: "Contenido", nosotros: "Redactado por especialistas del sector", media: "Generado en masa sin revisión" },
      { criterio: "Enlaces", nosotros: "Medios reales con tráfico verificable", media: "Directorios y redes privadas" },
      { criterio: "Velocidad de la web", nosotros: "Objetivo 0,3s con React", media: "WordPress con 20 plugins" },
      { criterio: "Medición", nosotros: "Facturación e ingresos atribuidos", media: "Posiciones capturadas a mano" },
    ],
    zonasTitle: "Empresas de Madrid con las que trabajamos",
    zonasIntro:
      "Damos servicio a empresas de toda el área metropolitana, con especial foco en los distritos de mayor densidad empresarial.",
    faqsTitle: "Posicionamiento web en Madrid: preguntas frecuentes",
    faqs: [
      {
        question: "¿Qué diferencia hay entre posicionamiento web y SEO local?",
        answer:
          "El posicionamiento web trabaja los resultados orgánicos clásicos para búsquedas de servicio, con o sin ciudad. El SEO local trabaja el mapa de Google y el pack de tres resultados que aparece cuando alguien busca cerca de su ubicación. Una empresa de Madrid con oficina física suele necesitar los dos; una empresa que vende a toda España, solo el primero.",
      },
      {
        question: "¿Necesito rehacer mi web para posicionar en Madrid?",
        answer:
          "No siempre. Auditamos primero: si el dominio tiene una base técnica razonable, se optimiza lo existente. Si la web arrastra una plantilla lenta, arquitectura caótica o contenido duplicado, rehacerla en React suele salir más rentable que parchear durante un año.",
      },
      {
        question: "¿Cuántas keywords se trabajan al mes?",
        answer:
          "No trabajamos por número de keywords sino por clústeres de intención. Un proyecto medio en Madrid arranca con 4 a 6 clústeres y entre 15 y 40 términos objetivo, ampliándose cada trimestre según lo que muestre el rendimiento real.",
      },
      marcaFaq,
    ],
    relatedTitle: "Sigue explorando",
    related: [
      { label: "Agencia SEO en Madrid", path: "/agencia-seo-madrid", text: "Visión completa del servicio en Madrid." },
      { label: "SEO Web con IA", path: "/servicios/seo-web-ia", text: "El servicio nacional en detalle." },
      { label: "Creación web en React", path: "/servicios/creacion-web-react", text: "Cuando la web actual es el cuello de botella." },
      { label: "Consultor SEO en Madrid", path: "/consultor-seo-madrid", text: "Si prefieres consultoría sin ejecución." },
    ],
    ctaTitle: "Pon a prueba tu posicionamiento actual",
    ctaText:
      "Te enviamos un diagnóstico con los tres problemas que hoy más te cuestan en tráfico cualificado y qué haría falta para resolverlos.",
  },

  "consultor-seo-madrid": {
    slug: "consultor-seo-madrid",
    badge: "Consultoría SEO senior en Madrid",
    h1: "Consultor SEO en Madrid.",
    h1Highlight: "Diagnóstico y dirección para tu equipo.",
    intro:
      "Consultoría SEO directa con el responsable del proyecto: sin cuentas intermedias, sin informes de plantilla. Diagnóstico, hoja de ruta y acompañamiento a tu equipo interno.",
    investment: "Propuesta personalizada tras diagnóstico · Diagnóstico puntual disponible",
    breadcrumbLabel: "Consultor SEO Madrid",
    metaTitle: "Consultor SEO en Madrid | SEOEVOLUTION",
    metaDescription:
      "Consultor SEO senior en Madrid. Diagnóstico técnico, hoja de ruta priorizada y acompañamiento a tu equipo interno. Propuesta según alcance y objetivos.",
    metaKeywords: "consultor seo madrid, consultoria seo madrid, experto seo madrid, freelance seo madrid",
    ogDescription:
      "Consultoría SEO senior en Madrid: diagnóstico, hoja de ruta priorizada y acompañamiento al equipo interno.",
    quickAnswerTitle: "¿Qué hace un consultor SEO en Madrid?",
    quickAnswer:
      "Un consultor SEO diagnostica por qué una web no capta tráfico cualificado, define la hoja de ruta priorizada y acompaña al equipo que la ejecuta. A diferencia de una agencia, no asume la ejecución completa: aporta criterio, prioriza y valida. Es la opción adecuada cuando ya existe un equipo interno de marketing o desarrollo en Madrid y lo que falta es dirección técnica.",
    serviceName: "Consultoría SEO en Madrid",
    serviceDescription:
      "Consultoría SEO senior para empresas de Madrid con equipo interno: auditoría, hoja de ruta, formación y validación de la ejecución.",
    pillarsTitle: "Cómo trabaja la consultoría",
    pillars: [
      { title: "Diagnóstico en 48 horas", text: "Auditoría técnica y competitiva entregada en dos días laborables." },
      { title: "Hoja de ruta priorizada", text: "Cada acción ordenada por impacto estimado y esfuerzo de implementación." },
      { title: "Acompañamiento al equipo", text: "Sesiones periódicas con marketing y desarrollo para desbloquear la ejecución." },
      { title: "Validación continua", text: "Revisamos lo implementado antes de que llegue a producción." },
    ],
    deliverablesTitle: "Qué recibes en la consultoría",
    deliverables: [
      "Auditoría técnica completa con capturas y evidencias reproducibles",
      "Análisis competitivo de los actores reales de tu sector en Madrid",
      "Mapa de keywords y arquitectura de contenidos recomendada",
      "Backlog priorizado por impacto/esfuerzo, listo para tu equipo",
      "Guía de redacción y plantillas para tu equipo de contenidos",
      "Sesión mensual de revisión y ajuste de prioridades",
      "Canal directo para dudas de implementación",
    ],
    comparisonTitle: "Consultoría o agencia: cuál te conviene",
    comparison: [
      { criterio: "Tienes equipo interno", nosotros: "Consultoría: aportamos criterio", media: "Agencia: duplicas funciones" },
      { criterio: "No tienes equipo", nosotros: "Agencia: ejecutamos nosotros", media: "Consultoría: el plan se queda en un PDF" },
      { criterio: "Propuesta", nosotros: "Según alcance y objetivos", media: "Paquete cerrado" },
      { criterio: "Velocidad de ejecución", nosotros: "Depende de tu equipo", media: "La marcamos nosotros" },
    ],
    zonasTitle: "Consultoría presencial y remota en Madrid",
    zonasIntro:
      "Sesiones remotas por defecto y presenciales cuando el proyecto lo justifica, en cualquier punto del área metropolitana.",
    faqsTitle: "Consultor SEO en Madrid: preguntas frecuentes",
    faqs: [
      {
        question: "¿Cuánto cobra un consultor SEO en Madrid?",
        answer:
          "Depende del formato (diagnóstico puntual o acompañamiento continuado), del tamaño del dominio y de la capacidad del equipo interno. En SEOEVOLUTION no publicamos tarifas: alcance e inversión se concretan tras el diagnóstico, según tu mercado, competencia y objetivos.",
      },
      {
        question: "¿Qué diferencia hay entre un consultor SEO y una agencia?",
        answer:
          "El consultor diagnostica, prioriza y dirige; la agencia además ejecuta. Si tienes redactores y desarrolladores en plantilla, la consultoría rinde más por euro invertido. Si no los tienes, el plan se queda sin implementar y conviene la agencia.",
      },
      {
        question: "¿Puedo empezar con consultoría y pasar a agencia después?",
        answer:
          "Sí, y es un camino habitual. El diagnóstico inicial se descuenta del primer mes si el proyecto pasa a formato agencia dentro de los 60 días siguientes.",
      },
      marcaFaq,
    ],
    relatedTitle: "Sigue explorando",
    related: [
      { label: "Agencia SEO en Madrid", path: "/agencia-seo-madrid", text: "Si prefieres que ejecutemos nosotros." },
      { label: "Posicionamiento web en Madrid", path: "/posicionamiento-web-madrid", text: "El servicio completo de orgánico." },
      { label: "Agencia GEO en Madrid", path: "/agencia-geo-madrid", text: "Visibilidad en motores de IA." },
      { label: "Sobre nosotros", path: "/sobre-nosotros", text: "Quién está detrás de la consultoría." },
    ],
    ctaTitle: "Reserva una sesión de diagnóstico",
    ctaText:
      "Analizamos tu dominio y te devolvemos las tres prioridades que más ingresos pueden mover en los próximos 90 días.",
  },

  "agencia-geo-madrid": {
    slug: "agencia-geo-madrid",
    badge: "GEO · Visibilidad en motores de IA",
    h1: "Agencia GEO en Madrid.",
    h1Highlight: "Visibilidad en respuestas de IA.",
    intro:
      "GEO es Generative Engine Optimization: trabajo de presencia en respuestas de IA para empresas de Madrid. Se apoya en las bases SEO, información verificable y observación de menciones y fuentes, sin garantizar citas.",
    investment: "Propuesta personalizada tras diagnóstico · Plazas limitadas",
    breadcrumbLabel: "Agencia GEO Madrid",
    metaTitle: "Agencia GEO en Madrid | Visibilidad en IA | SEOEVOLUTION",
    metaDescription:
      "GEO para empresas de Madrid: base SEO, contenido útil y evaluación de menciones y fuentes en respuestas generativas. Propuesta personalizada; mínimo de 6 meses.",
    metaKeywords:
      "agencia geo madrid, geo madrid, visibilidad chatgpt madrid, seo para ia madrid, generative engine optimization",
    ogDescription:
      "GEO para empresas de Madrid: base SEO, contenido útil y observación de menciones y fuentes en respuestas de ChatGPT, Perplexity y Gemini.",
    quickAnswerTitle: "¿Qué es el GEO y en qué se diferencia del SEO?",
    quickAnswer:
      "GEO (Generative Engine Optimization) es el trabajo de optimización para que los motores generativos —ChatGPT, Perplexity, Gemini y los AI Overviews de Google— citen a una marca en sus respuestas. El SEO clásico persigue el clic en un listado de resultados; el GEO persigue la mención dentro de una respuesta redactada por un modelo. Comparten base técnica, pero el GEO añade trabajo de entidades, respuestas directas citables y presencia en fuentes que los modelos consultan.",
    serviceName: "Agencia GEO en Madrid",
    serviceDescription:
      "Optimización para motores generativos (ChatGPT, Perplexity, Gemini, AI Overviews) para empresas de Madrid.",
    pillarsTitle: "Los cuatro frentes del GEO",
    pillars: [
      { title: "Entidad de marca", text: "Que los modelos sepan quién eres, a qué te dedicas y dónde operas, sin ambigüedad." },
      { title: "Contenido citable", text: "Respuestas claras y datos propios contrastables; la extensión se adapta a la pregunta y no garantiza citas." },
      { title: "Datos estructurados", text: "Schema completo y coherente: Organization, Service, FAQPage, LocalBusiness." },
      { title: "Presencia en fuentes", text: "Los modelos citan medios, directorios y foros concretos. Trabajamos esas fuentes." },
    ],
    deliverablesTitle: "Qué ejecutamos en un proyecto GEO",
    deliverables: [
      "Auditoría de menciones actuales en ChatGPT, Perplexity, Gemini y AI Overviews",
      "Construcción de la entidad de marca y consistencia NAP en todo el ecosistema",
      "Reescritura de páginas clave en formato de respuesta directa citable",
      "Implementación y validación de datos estructurados avanzados",
      "Publicación de datos propios y contenido de referencia que los modelos puedan citar",
      "Trabajo de presencia en las fuentes que cada motor consulta con más frecuencia",
      "Revisión del acceso de rastreadores y contenido visible; sin archivos especiales obligatorios",
      "Informe mensual de menciones y presencia en la muestra frente a competidores",
    ],
    comparisonTitle: "SEO clásico frente a GEO",
    comparison: [
      { criterio: "Objetivo", nosotros: "GEO: ser citado en la respuesta", media: "SEO: ser clicado en el listado" },
      { criterio: "Unidad de medida", nosotros: "Menciones y presencia en la muestra", media: "Posiciones y clics" },
      { criterio: "Formato de contenido", nosotros: "Respuesta directa y datos propios", media: "Artículo largo optimizado" },
      { criterio: "Señales clave", nosotros: "Entidad, schema y fuentes citadas", media: "Enlaces y autoridad de dominio" },
      { criterio: "Recomendación", nosotros: "Trabajar ambos a la vez", media: "Alcance según el diagnóstico" },
    ],
    zonasTitle: "GEO para empresas de Madrid",
    zonasIntro:
      "Ejemplos de preguntas a analizar en Madrid: 'cómo elegir una clínica dental en Chamberí' o 'qué revisar al contratar asesoría fiscal en Salamanca'. Son hipótesis de consulta, no volúmenes ni resultados observados.",
    faqsTitle: "Agencia GEO en Madrid: preguntas frecuentes",
    faqs: [
      {
        question: "¿El GEO sustituye al SEO?",
        answer:
          "No. GEO se apoya en acceso técnico y contenido útil. Google mantiene las bases SEO habituales para AI Overviews y AI Mode, sin exigir archivos ni schema especiales ni garantizar aparición; otros motores pueden usar distintas fuentes y modos de búsqueda.",
      },
      {
        question: "¿Cómo se mide el resultado de un proyecto GEO?",
        answer:
          "Con un panel de consultas representativas que se lanzan mensualmente contra cada motor, registrando si la marca aparece, en qué posición dentro de la respuesta y frente a qué competidores. La muestra no equivale a una posición media ni a una cuota de mercado; las respuestas pueden variar.",
      },
      {
        question: "¿Cuánto tarda en verse una mención en ChatGPT o Perplexity?",
        answer:
          "No existe un plazo universal: depende del motor, la consulta, el contexto y las fuentes. El mínimo de GEO es de 6 meses de trabajo y evaluación, sin garantía de aparición. Después, la continuidad es opcional y a convenir; no depende de reentrenar modelos que no controlamos.",
      },
      marcaFaq,
    ],
    relatedTitle: "Sigue explorando",
    related: [
      { label: "GEO + SEO 360", path: "/servicios/geo-seo-360", text: "El servicio nacional completo." },
      { label: "Agencia SEO en Madrid", path: "/agencia-seo-madrid", text: "El plan orgánico clásico." },
      { label: "SEO local vs GEO", path: "/seo-local-vs-geo-posicionamiento", text: "Diferencias explicadas en detalle." },
      { label: "Blog: tendencias SEO 2026", path: "/blog/tendencias-seo-2026", text: "Hacia dónde va la búsqueda con IA." },
    ],
    ctaTitle: "¿Te nombra hoy la IA cuando preguntan por tu sector?",
    ctaText:
      "Lanzamos un panel de consultas reales en ChatGPT, Perplexity y Gemini y te enseñamos exactamente a quién están citando en Madrid.",
  },
};
